import { parseBundle, parseScriptLine } from '@drama/contracts'

/**
 * @typedef {import('@drama/contracts/drama').BundleT} Bundle
 * @typedef {import('@drama/contracts/drama').ScriptLineT} ScriptLine
 */

/**
 * @typedef {object} ContinueReq
 * @property {string} drama_id
 * @property {string} episode_id
 * @property {string} [prompt] 用户打断/篡改剧情的输入
 */

/**
 * 规范化语速：空串 / 非数值 / 非正数全部丢弃，避免把脏参数发到后端。
 * @param {unknown} value
 * @returns {number|undefined}
 */
function normalizeSpeechRate(value) {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) return value
  if (typeof value === 'string') {
    const n = Number(value)
    if (Number.isFinite(n) && n > 0) return n
  }
  return undefined
}

/**
 * drama 域：可播放 bundle 拉取、分集列表、续写流。
 * @param {import('../transport.js').createTransport extends (...a:any)=>infer R ? R : any} transport
 */
export function createDramaApi(transport) {
  return {
    /**
     * @param {string} id
     * @returns {Promise<Bundle>}
     */
    getBundle(id) {
      return transport.request('GET', `/api/sk/drama/${encodeURIComponent(id)}/bundle`, {
        parse: parseBundle,
      })
    },

    /**
     * 可播 bundle（原始，不过 motion-comic 契约裁剪）——已发布的剧可能是「富 rig bundle」
     * （含 characters/scene/cast，供 <DramaPlayer> 真骨骼引擎消费），其形状是 motion-comic
     * Bundle 的超集，不能用 parseBundle 裁剪。播放器据返回里有无 `characters` 自选渲染路径。
     * @param {string} id
     * @param {number} [ep] 剧集号（不同剧情走向，默认根集 1）。各集自带完整 script+bundle。
     */
    getPlayBundle(id, ep) {
      const q = ep && ep > 1 ? `?ep=${ep}` : ''
      return transport.request('GET', `/api/sk/drama/${encodeURIComponent(id)}/bundle${q}`)
    },

    /**
     * @param {string} dramaId
     * @returns {Promise<any[]>}
     */
    listEpisodes(dramaId) {
      return transport.request('GET', `/api/sk/drama/${encodeURIComponent(dramaId)}/episodes`)
    },

    /**
     * 单行配音：调后端豆包 TTS，返 { mime, bytes, speaker, audioUrl }（audioUrl 为 data: base64 mp3）。
     * 不直连厂商（红线）——经 api-sdk → 后端 /api/sk/drama/:id/line/tts。
     * `instruction` 是语气指令（复刻音色靠它控语气）；`emotion`/`emotionScale` 走普通音色情绪分支。
     * 全链路透传 instruction 是「钟爹男声 / 语气强度」生效的关键（旧实现多处丢了 instruction）。
     * @param {string} dramaId
     * @param {{ text: string, speaker?: string, instruction?: string, emotion?: string, emotion_scale?: number, speech_rate?: number }} line
     * @returns {Promise<{ mime: string, bytes: number, speaker: string, audio_url: string }>}
     */
    lineTts(dramaId, line) {
      return transport.request(
        'POST',
        `/api/sk/drama/${encodeURIComponent(dramaId)}/line/tts`,
        {
          body: {
            text: line.text,
            speaker: line.speaker,
            instruction: line.instruction,
            emotion: line.emotion,
            emotion_scale: line.emotion_scale ?? line.emotionScale,
            speech_rate: normalizeSpeechRate(line.speech_rate ?? line.speechRate),
          },
        },
      )
    },

    /**
     * 续写流：NDJSON，逐行经 contracts 校验后 yield `ScriptLine`。
     * @param {ContinueReq} req
     * @returns {AsyncIterable<ScriptLine>}
     */
    streamContinue(req) {
      return transport.stream('POST', '/api/sk/drama/continue', {
        body: req,
        parse: parseScriptLine,
      })
    },
  }
}
